import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './authStore';
import type { 
  Room, 
  ChatMessage, 
  GameRound, 
  RoundResult, 
  GameResult,
  RoomPreview,
  RoomSettings,
  PlayerScore,
  ModeConfig,
  GameMode,
  Team,
  QuizQuestion
} from '@shared/types';

// Historique d'un round joué
export interface PlayedRound {
  round: number;
  title: string;
  artist: string;
  albumCover?: string;
}

// Statut des réponses des joueurs pour le round en cours
export interface PlayerAnswerStatus {
  playerId: string;
  playerName: string;
  titleFound: boolean;
  artistFound: boolean;
}

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  
  // Room state
  currentRoom: Room | null;
  publicRooms: RoomPreview[];
  hasLeftRoom: boolean;  // Flag pour empêcher le rejoin auto
  
  // Game state
  isPlaying: boolean;
  isWaitingForPlayers: boolean;  // Attente avant le premier round
  currentRound: GameRound | null;
  roundResult: RoundResult | null;
  gameResult: GameResult | null;
  scores: PlayerScore[];
  myAnswer: { titleFound: boolean; artistFound: boolean } | null;
  playedRounds: PlayedRound[];  // Historique des chansons jouées
  playerAnswers: PlayerAnswerStatus[];  // Réponses des joueurs pour le round en cours
  
  // Game modes state
  gameMode: GameMode;
  modeConfig: ModeConfig | null;
  playerLives: Record<string, number>;  // Battle Royale
  eliminatedPlayers: string[];           // Battle Royale
  isEliminated: boolean;                 // Local player eliminated
  teams: Team[];                         // Mode Équipes
  teamScores: Record<string, number>;    // Mode Équipes
  currentQuestion: QuizQuestion | null;  // Mode Quiz
  quizAnswered: boolean;                 // Has answered quiz
  
  // Chat
  messages: ChatMessage[];
  
  // Actions
  connect: () => void;
  disconnect: () => void;
  
  // Player actions
  authAsGuest: (name: string) => void;
  authWithSpotify: (token: string) => void;
  
  // Room actions
  createRoom: (name: string, playlistId: string, settings?: Partial<RoomSettings>, isPrivate?: boolean) => void;
  joinRoom: (code: string) => void;
  leaveRoom: () => void;
  resetLeftRoom: () => void;
  kickPlayer: (playerId: string) => void;
  updatePlaylist: (playlistId: string, playlistName?: string) => void;
  fetchPublicRooms: () => void;
  
  // Game actions
  startGame: (tracks?: any[]) => void;
  submitAnswer: (answer: string) => void;
  submitQuizAnswer: (answerId: string) => void;  // Mode Quiz
  usePowerUp: (type: string, targetId?: string) => void;
  
  // Chat actions
  sendMessage: (message: string) => void;
  
  // Reset
  resetGame: () => void;
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  
  currentRoom: null,
  publicRooms: [],
  hasLeftRoom: false,
  
  isPlaying: false,
  isWaitingForPlayers: false,
  currentRound: null,
  roundResult: null,
  playedRounds: [],
  gameResult: null,
  scores: [],
  myAnswer: null,
  playerAnswers: [],
  
  // Game modes initial state
  gameMode: 'classic',
  modeConfig: null,
  playerLives: {},
  eliminatedPlayers: [],
  isEliminated: false,
  teams: [],
  teamScores: {},
  currentQuestion: null,
  quizAnswered: false,
  
  messages: [],
  
  connect: () => {
    const socket = io({
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    
    socket.on('connect', () => {
      console.log('🔌 Socket connecté');
      set({ isConnected: true });
      
      // Récupérer la liste des rooms publiques au connect
      socket.emit('room:list');
      
      // Si un player existe déjà dans authStore, l'authentifier automatiquement
      const authState = useAuthStore.getState();
      console.log('🔐 Auth state:', authState.player?.name, authState.isAuthenticated);
      if (authState.player && authState.isAuthenticated) {
        // Utilisateur authentifié avec un token JWT (email/password ou Google)
        if (authState.token && authState.user) {
          console.log('📤 Envoi auth registered:', authState.player.name);
          socket.emit('player:auth', { 
            type: 'registered', 
            token: authState.token,
            userId: authState.user.id,
            name: authState.player.name,
            avatar: authState.player.avatar,
            spotifyId: authState.user.spotify_id || undefined,
          });
        }
        // Utilisateur connecté via OAuth Spotify directement (sans compte)
        else if (authState.player.type === 'spotify' && authState.spotifyToken) {
          console.log('📤 Envoi auth spotify:', authState.player.name, authState.player.avatar);
          socket.emit('player:auth', { 
            type: 'spotify', 
            spotifyToken: authState.spotifyToken,
            spotifyId: authState.player.spotifyId,
            name: authState.player.name,
            avatar: authState.player.avatar,
          });
        } else if (authState.player.type === 'guest') {
          socket.emit('player:auth', { type: 'guest', guestName: authState.player.name });
        }
      }
    });
    
    socket.on('disconnect', () => {
      console.log('🔌 Socket déconnecté');
      set({ isConnected: false });
    });
    
    // Player events
    socket.on('player:authenticated', ({ player }) => {
      console.log('✅ Authentifié:', player.name);
      // Mettre à jour le authStore avec le player retourné par le serveur
      useAuthStore.getState().setPlayer(player);
    });
    
    socket.on('player:error', ({ message }) => {
      console.error('❌ Erreur player:', message);
    });
    
    // Room events
    socket.on('room:created', ({ room }) => {
      set({ currentRoom: room, hasLeftRoom: false });
    });
    
    socket.on('room:joined', ({ room }) => {
      set({ currentRoom: room, hasLeftRoom: false });
    });
    
    socket.on('room:left', () => {
      set({
        currentRoom: null,
        messages: [],
        hasLeftRoom: true,
        // Clear game state to prevent stale rejoin
        isPlaying: false,
        isWaitingForPlayers: false,
        currentRound: null,
        roundResult: null,
        gameResult: null,
        scores: [],
        myAnswer: null,
        playedRounds: [],
        playerAnswers: [],
      });
    });
    
    socket.on('room:kicked', () => {
      set({ currentRoom: null, messages: [] });
    });
    
    socket.on('room:playerJoined', ({ player }) => {
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            players: [...state.currentRoom.players, player],
          },
        };
      });
    });
    
    socket.on('room:playerLeft', ({ playerId }) => {
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            players: state.currentRoom.players.filter(p => p.id !== playerId),
          },
        };
      });
    });
    
    socket.on('room:list', ({ rooms }) => {
      set({ publicRooms: rooms });
    });
    
    socket.on('room:playlistUpdated', ({ playlistId, playlistName }: { playlistId: string; playlistName: string }) => {
      console.log('🎵 Playlist mise à jour:', playlistName);
      set(state => {
        if (!state.currentRoom) return state;
        return {
          currentRoom: {
            ...state.currentRoom,
            playlistId,
          },
        };
      });
    });
    
    socket.on('room:error', ({ message }) => {
      console.error('❌ Erreur room:', message);
    });
    
    // Game events
    socket.on('game:started', ({ totalRounds: _totalRounds, settings: _settings, gameMode, modeConfig, teams, playerLives }) => {
      set({ 
        isPlaying: true,
        isWaitingForPlayers: true,  // En attente du premier round
        gameResult: null,
        scores: [],
        playedRounds: [],  // Reset de l'historique
        playerAnswers: [],  // Reset des réponses
        // Mode state
        gameMode: gameMode || 'classic',
        modeConfig: modeConfig || null,
        teams: teams || [],
        teamScores: {},
        playerLives: playerLives || {},
        eliminatedPlayers: [],
        isEliminated: false,
        currentQuestion: null,
        quizAnswered: false,
      });
    });
    
    socket.on('game:roundStart', (round) => {
      set({ 
        currentRound: round, 
        roundResult: null,
        myAnswer: null,
        isWaitingForPlayers: false,  // Plus en attente
        playerAnswers: [],  // Reset des réponses pour le nouveau round
        // Mode state
        currentQuestion: round.question || null,
        quizAnswered: false,
        playerLives: round.playerLives || {},
        eliminatedPlayers: round.eliminatedPlayers || [],
        teamScores: round.teamScores || {},
        teams: round.teams || [],
      });
    });
    
    // Un joueur a trouvé quelque chose
    socket.on('game:playerScored', (data: { playerId: string; playerName: string; titleFound: boolean; artistFound: boolean }) => {
      set(state => {
        // Trouver ou créer l'entrée pour ce joueur
        const existingIndex = state.playerAnswers.findIndex(p => p.playerId === data.playerId);
        if (existingIndex >= 0) {
          // Mettre à jour
          const newAnswers = [...state.playerAnswers];
          newAnswers[existingIndex] = {
            ...newAnswers[existingIndex],
            titleFound: data.titleFound,
            artistFound: data.artistFound,
          };
          return { playerAnswers: newAnswers };
        } else {
          // Ajouter
          return { 
            playerAnswers: [...state.playerAnswers, {
              playerId: data.playerId,
              playerName: data.playerName,
              titleFound: data.titleFound,
              artistFound: data.artistFound,
            }]
          };
        }
      });
    });
    
    socket.on('game:roundEnd', (result) => {
      // Ajouter le round à l'historique
      const newPlayedRound: import('./socketStore').PlayedRound = {
        round: result.round,
        title: result.answer.title,
        artist: result.answer.artist,
        albumCover: result.answer.albumCover,
      };
      
      set(state => ({ 
        roundResult: result,
        currentRound: null,
        scores: result.scores,
        playedRounds: [...state.playedRounds, newPlayedRound],
        // Update mode state from result
        playerLives: result.playerLives || state.playerLives,
        eliminatedPlayers: result.eliminatedPlayers || state.eliminatedPlayers,
        teamScores: result.teamScores || state.teamScores,
      }));
    });
    
    socket.on('game:ended', (result) => {
      set({ 
        gameResult: result, 
        isPlaying: false,
        currentRound: null,
        scores: result.scores,
        teams: result.teamRanking || [],
        teamScores: result.teamScores || {},
      });
    });
    
    // Battle Royale: joueur éliminé
    socket.on('game:eliminated', ({ reason }) => {
      console.log('💀 Vous êtes éliminé:', reason);
      set({ isEliminated: true });
    });
    
    socket.on('game:playerEliminated', ({ playerId, playerName, playersRemaining }) => {
      console.log(`💀 ${playerName} éliminé ! ${playersRemaining} joueurs restants`);
      set(state => ({
        eliminatedPlayers: [...state.eliminatedPlayers, playerId],
      }));
    });
    
    // Quiz mode
    socket.on('game:quizResult', ({ correct, pointsEarned, correctAnswerId: _correctId }) => {
      console.log('📝 Quiz result:', correct ? `+${pointsEarned}` : 'incorrect');
      set({ quizAnswered: true });
    });
    
    socket.on('game:playerAnsweredQuiz', ({ playerId: _playerId, playerName }) => {
      console.log(`📝 ${playerName} a répondu`);
    });
    
    socket.on('game:answerResult', (result) => {
      set({ 
        myAnswer: { 
          titleFound: result.titleFound, 
          artistFound: result.artistFound 
        } 
      });
    });
    
    socket.on('game:error', ({ message }) => {
      console.error('❌ Erreur game:', message);
    });
    
    // Chat events
    socket.on('chat:message', (message) => {
      // Play notification sound if message is from someone else
      const currentPlayer = useAuthStore.getState().player;
      if (message.playerId && currentPlayer && message.playerId !== currentPlayer.id && !message.isSystem) {
        try {
          const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 800;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          osc.start();
          osc.stop(ctx.currentTime + 0.3);
        } catch {}
      }
      set(state => ({
        messages: [...state.messages.slice(-99), message], // Garder 100 messages max
      }));
    });
    
    set({ socket });
  },
  
  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },
  
  // Player actions
  authAsGuest: (name) => {
    const { socket } = get();
    if (socket) {
      socket.emit('player:auth', { type: 'guest', guestName: name });
    }
  },
  
  authWithSpotify: (token) => {
    const { socket } = get();
    if (socket) {
      socket.emit('player:auth', { type: 'spotify', spotifyToken: token });
    }
  },
  
  // Room actions
  createRoom: (name, playlistId, settings, isPrivate) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:create', { name, playlistId, settings, isPrivate });
    }
  },
  
  joinRoom: (code) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:join', { code });
    }
  },
  
  leaveRoom: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:leave');
    }
  },
  
  resetLeftRoom: () => {
    set({ hasLeftRoom: false });
  },
  
  kickPlayer: (playerId) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:kick', { playerId });
    }
  },
  
  updatePlaylist: (playlistId, playlistName) => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:updatePlaylist', { playlistId, playlistName });
    }
  },
  
  fetchPublicRooms: () => {
    const { socket } = get();
    if (socket) {
      socket.emit('room:list');
    }
  },
  
  // Game actions
  startGame: (tracks) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:start', { tracks });
    }
  },
  
  submitAnswer: (answer) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:answer', { answer });
    }
  },
  
  submitQuizAnswer: (answerId) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:quizAnswer', { answerId });
    }
  },
  
  usePowerUp: (type, targetId) => {
    const { socket } = get();
    if (socket) {
      socket.emit('game:usePowerUp', { type, targetId });
    }
  },
  
  // Chat actions
  sendMessage: (message) => {
    const { socket } = get();
    if (socket) {
      socket.emit('chat:message', { message });
    }
  },
  
  // Reset
  resetGame: () => {
    set({
      isPlaying: false,
      isWaitingForPlayers: false,
      currentRound: null,
      roundResult: null,
      gameResult: null,
      scores: [],
      myAnswer: null,
      playedRounds: [],
      // Reset mode state
      gameMode: 'classic',
      modeConfig: null,
      playerLives: {},
      eliminatedPlayers: [],
      isEliminated: false,
      teams: [],
      teamScores: {},
      currentQuestion: null,
      quizAnswered: false,
    });
  },
}));
